import type { TicketRecoveryReason, UpdateTicketData } from '@field-tickets/shared';
import { showToast } from '@/components/Toast';
import { pruneExpiredTicketStorage, writeTicketStorage } from '@/utils/ticketStorageRetention';
import { describeUnsavedTicketEdits, type TicketConflictSnapshot } from '@/utils/offlineQueue';
import {
  activeTicketRecoveryOwner,
  TICKET_RECOVERY_MAX_AGE_MS,
  TICKET_RECOVERY_STORAGE_WARNING,
  TICKET_RECOVERY_TAB_ID,
  ticketRecoveryStorageKey,
  type TicketRecoveryDraft,
} from '@/utils/ticketRecovery';

/**
 * Retained drafts of a ticket (#1041): a local copy of edits the server has
 * not confirmed, kept so they can be restored. Not a recovery draft.
 *
 * Written when a dirty QuickBooks-linked form is hidden or closed (those
 * edits never enter the offline queue), and by a recovery Reload, which
 * keeps every recovery draft's edits here instead of deleting the only copy.
 * A retained draft never blocks a PUT, never enters the offline queue or
 * its replay and never puts a form in recovery; nothing ever sends it. The
 * user restores it (or discards it) from the ticket's notice.
 *
 * Same identity rules as recovery drafts: one owner and one slot per key,
 * `ft-ticket-retained:<user>:<ticket>:<slot>` (disjoint from the recovery
 * prefix, so the PUT gate never sees them). The slot is `<tab>:<form>` for
 * a kept-on-leave draft (one per tab and form session, so a form never
 * overwrites edits another form kept, even in the same tab) and
 * `reload:<tab>:<createdAt>` for a draft kept by a Reload. Read back only for its own owner (key and payload owner), stored
 * synchronously in localStorage with an in-memory fallback and a warning,
 * and dropped 30 days after its last write.
 */
export interface TicketRetainedDraft {
  owner: string;
  /** The tab whose form wrote the edits (a `queue:` id for a replayed save). */
  tabId: string;
  /** The storage key's last segment. */
  slot: string;
  ticketId: string;
  /** The edits, pinned to the version token they were based on. */
  payload: UpdateTicketData;
  baseSnapshot?: TicketConflictSnapshot;
  /**
   * The payload the form held for the base version (same shaping and role
   * scoping), including fields the conflict snapshot lacks. Absent for
   * drafts from paths with no form.
   */
  basePayload?: Record<string, unknown>;
  /** `payload` as copyable text. */
  summary: string;
  createdAt: number;
  /** Set when a recovery Reload kept these edits. */
  recoveryReason?: TicketRecoveryReason;
  /** Fields the server changed, when the recovery knew them. */
  changedFields?: string[];
}

/** Fired on window when a retained draft is written or removed in this tab. */
export const TICKET_RETAINED_EVENT = 'ft:ticket-retained';
const STORAGE_PREFIX = 'ft-ticket-retained:';
/**
 * Drafts localStorage refused, kept in memory only. Stored drafts are always
 * read from storage, so another tab's Discard is never undone here.
 */
const memoryDrafts = new Map<string, TicketRetainedDraft>();

function ticketPrefix(owner: string, ticketId: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(owner)}:${encodeURIComponent(ticketId)}:`;
}

export function ticketRetainedStorageKey(owner: string, ticketId: string, slot: string): string {
  return `${ticketPrefix(owner, ticketId)}${encodeURIComponent(slot)}`;
}

/** A new kept-on-leave slot for a form session in this tab. */
export function newTicketRetainedSlot(): string {
  let id = '';
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') id = crypto.randomUUID();
  } catch {
    // Fall through.
  }
  return `${TICKET_RECOVERY_TAB_ID}:${id || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`}`;
}

/** True when a storage key belongs to this owner's retained drafts of this ticket. */
export function isTicketRetainedStorageKey(key: string | null, owner: string, ticketId: string): boolean {
  return Boolean(key && key.startsWith(ticketPrefix(owner, ticketId)));
}

/** A form's kept-on-leave draft of its current edits, in its own slot. */
export function buildTicketRetainedDraft(
  owner: string,
  ticketId: string,
  slot: string,
  payload: UpdateTicketData,
  baseSnapshot: TicketConflictSnapshot | undefined,
  basePayload?: Record<string, unknown>,
): TicketRetainedDraft {
  return {
    owner,
    tabId: TICKET_RECOVERY_TAB_ID,
    slot,
    ticketId,
    payload,
    baseSnapshot,
    basePayload,
    summary: describeUnsavedTicketEdits(payload, baseSnapshot),
    createdAt: Date.now(),
  };
}

function notify(ticketId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TICKET_RETAINED_EVENT, { detail: { ticketId } }));
}

function storageKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
  } catch {
    // No storage.
  }
  return keys;
}

function removeKey(key: string): void {
  memoryDrafts.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing more to remove.
  }
}

function readStored(key: string): unknown {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null');
  } catch {
    return null;
  }
}

function isExpired(createdAt: unknown, now: number): boolean {
  return !(typeof createdAt === 'number' && now - createdAt < TICKET_RECOVERY_MAX_AGE_MS);
}

function isDraft(value: unknown, owner: string, ticketId: string): value is TicketRetainedDraft {
  const draft = value as Partial<TicketRetainedDraft> | null;
  return Boolean(
    draft &&
    draft.owner === owner &&
    draft.ticketId === ticketId &&
    typeof draft.tabId === 'string' &&
    typeof draft.slot === 'string' &&
    typeof draft.summary === 'string' &&
    draft.payload &&
    typeof draft.payload === 'object',
  );
}

/**
 * Writes a retained draft under its slot. When localStorage refuses it the
 * draft is kept in memory and the user is warned that it lasts only until
 * the tab closes. Returns whether it was stored durably.
 */
export function writeTicketRetainedDraft(draft: TicketRetainedDraft): boolean {
  const durable = storeDraft(draft);
  if (!durable) showToast(TICKET_RECOVERY_STORAGE_WARNING, 'warning');
  notify(draft.ticketId);
  return durable;
}

function storeDraft(draft: TicketRetainedDraft): boolean {
  pruneExpiredTicketStorage();
  const now = Date.now();
  for (const [key, kept] of memoryDrafts) if (isExpired(kept.createdAt, now)) removeKey(key);
  const key = ticketRetainedStorageKey(draft.owner, draft.ticketId, draft.slot);
  try {
    writeTicketStorage(key, JSON.stringify(draft));
    memoryDrafts.delete(key);
    return true;
  } catch {
    // Storage refused (private mode, quota): this page keeps it in memory.
    memoryDrafts.set(key, draft);
    return false;
  }
}

/** Every retained draft of this ticket for this user, oldest first. */
export function listTicketRetainedDrafts(owner: string | null, ticketId: string): TicketRetainedDraft[] {
  if (!owner || !ticketId) return [];
  const prefix = ticketPrefix(owner, ticketId);
  const now = Date.now();
  const byKey = new Map<string, TicketRetainedDraft>();
  for (const [key, draft] of memoryDrafts) {
    if (key.startsWith(prefix) && isDraft(draft, owner, ticketId)) byKey.set(key, draft);
  }
  for (const key of storageKeys()) {
    if (!key.startsWith(prefix) || byKey.has(key)) continue;
    const parsed = readStored(key);
    // The owner inside the draft must match too: a key alone is not trusted.
    if (isDraft(parsed, owner, ticketId)) byKey.set(key, parsed);
  }
  for (const [key, draft] of byKey) {
    if (isExpired(draft.createdAt, now)) {
      removeKey(key);
      byKey.delete(key);
    }
  }
  return [...byKey.values()].sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Removes one retained draft. With `createdAt`, only that exact write: a
 * newer draft in the same slot (a later hide, another mount) stays.
 */
export function removeTicketRetainedDraft(owner: string | null, ticketId: string, slot: string, createdAt?: number): void {
  if (!owner) return;
  const key = ticketRetainedStorageKey(owner, ticketId, slot);
  const current = (memoryDrafts.get(key) ?? readStored(key)) as Partial<TicketRetainedDraft> | null;
  if (!current || (createdAt !== undefined && current.createdAt !== createdAt)) return;
  removeKey(key);
  notify(ticketId);
}

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * A recovery Reload keeps the recovery drafts' edits as retained drafts
 * (their tab, time, payload and base unchanged) before it clears them. Edits
 * an earlier Reload already kept are not stored twice.
 *
 * `durable` is false when an edit stored durably as a recovery draft could
 * only be kept in memory here: then that copy is dropped, the recovery draft
 * is left untouched, and the caller must not clear it. Nothing here ever
 * deletes or rewrites a recovery draft.
 */
export function retainTicketRecoveryDrafts(drafts: TicketRecoveryDraft[]): { kept: number; durable: boolean } {
  let kept = 0;
  let durable = true;
  let warn = false;
  const notified = new Set<string>();
  for (const draft of drafts) {
    const existing = listTicketRetainedDrafts(draft.owner, draft.ticketId);
    const payload = JSON.stringify(draft.payload);
    const original = readRaw(ticketRecoveryStorageKey(draft.owner, draft.ticketId, draft.tabId));
    // Only reload slots: a form's own slot may still be rewritten by it.
    const same = existing.find((retained) => retained.slot.startsWith('reload:') && JSON.stringify(retained.payload) === payload);
    if (same && (original === null || !memoryDrafts.has(ticketRetainedStorageKey(same.owner, same.ticketId, same.slot)))) {
      kept += 1;
      continue;
    }
    const retained: TicketRetainedDraft = {
      owner: draft.owner,
      tabId: draft.tabId,
      slot: `reload:${draft.tabId}:${draft.createdAt}`,
      ticketId: draft.ticketId,
      payload: draft.payload,
      baseSnapshot: draft.baseSnapshot,
      basePayload: draft.basePayload,
      summary: draft.summary,
      createdAt: draft.createdAt,
      recoveryReason: draft.reason,
      changedFields: draft.changedFields,
    };
    const stored = storeDraft(retained);
    notified.add(draft.ticketId);
    if (!stored && original !== null) {
      // Never trade a stored copy for one that ends with this tab.
      memoryDrafts.delete(ticketRetainedStorageKey(retained.owner, retained.ticketId, retained.slot));
      durable = false;
      continue;
    }
    if (!stored) warn = true;
    kept += 1;
  }
  if (warn) showToast(TICKET_RECOVERY_STORAGE_WARNING, 'warning');
  for (const ticketId of notified) notify(ticketId);
  return { kept, durable };
}

/** True when a retained draft of this ticket could only be kept in memory. */
export function ticketRetainedDraftsMemoryOnly(owner: string | null, ticketId: string): boolean {
  if (!owner) return false;
  const prefix = ticketPrefix(owner, ticketId);
  return [...memoryDrafts.keys()].some((key) => key.startsWith(prefix));
}

/** Only the signed-in owner may disclose memory-only retained edits. */
export function memoryOnlyTicketRetainedText(owner: string | null): string {
  if (!owner || owner !== activeTicketRecoveryOwner()) return '';
  return [...memoryDrafts.values()]
    .filter((draft) => draft.owner === owner)
    .map((draft) => `Ticket ${draft.ticketId}\n${draft.summary}`).join('\n\n');
}

export function __resetTicketRetainedDraftsForTest(): void {
  memoryDrafts.clear();
  for (const key of storageKeys()) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing more to clear.
    }
  }
}
